import { DateOnly } from '@if/core/date'
import { formatUtcIsoDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { ControlledDateOnlyPicker, type ErrorOverrides, useFormDialog } from '@if/mui'
import { useUpdateActiveCustomBuydownPlan } from '@src/data/api/custom-buydown-api/custom-buydown-api'
import { CustomBuydownPlan } from '@src/types/CustomBuydown'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { getPromoPlanErrorCode, PromoPlanErrorCode } from '../promoPlanErrorCodes'
import { buildWindowOverlapMessage } from '../windowEditErrors'

const setEndDateSchema = () =>
  yup.object({
    expiresOn: yup.dateOnly().defined().nullable(),
  })

type SetEndDateFormValues = yup.InferSubmit<ReturnType<typeof setEndDateSchema>>

export const useSetActivePlanEndDateDialog = (merchantId: string, scheduledPlan?: CustomBuydownPlan | null) => {
  const { t } = useTranslation()
  const updateActivePlan = useUpdateActiveCustomBuydownPlan(merchantId)

  const onError: ErrorOverrides = {
    notFound: (_descriptor, t) => ({ role: 'contextual', message: t('customBuydown.errors.windowEditPlanNotFound') }),
    unknown: (descriptor, t) => {
      const code = getPromoPlanErrorCode(descriptor)
      if (code === PromoPlanErrorCode.EndDateInPast) {
        return { role: 'contextual', message: t('customBuydown.errors.endDateInPast') }
      }
      if (code === PromoPlanErrorCode.PlanAlreadyEnded) {
        return { role: 'contextual', message: t('customBuydown.errors.windowEditPlanAlreadyEnded') }
      }
      if (code === PromoPlanErrorCode.PlanNotYetActive) {
        return { role: 'contextual', message: t('customBuydown.errors.windowEditPlanNotYetActive') }
      }
      return undefined
    },
    conflict: (descriptor, t) => buildWindowOverlapMessage(descriptor, scheduledPlan, t),
  }

  return useFormDialog<SetEndDateFormValues, CustomBuydownPlan, CustomBuydownPlan>({
    icon: Calendar,
    title: (ctx) => t('customBuydown.timeline.setEndDateTitle', { planCode: ctx.props.label }),
    schema: setEndDateSchema(),
    defaultValues: (plan) => ({ expiresOn: plan.expiresOn ? new DateOnly(plan.expiresOn) : null }),
    onError,
    content: (ctx) => (
      <ControlledDateOnlyPicker
        control={ctx.form.control}
        name="expiresOn"
        label={t('customBuydown.timeline.ends')}
        clearable
        fullWidth
      />
    ),
    successMessage: (saved) => t('customBuydown.timeline.editDatesSaved', { planCode: saved.label }),
    onSubmit: async (data, ctx) =>
      updateActivePlan.mutateAsync({
        promoPlanId: ctx.props.id,
        dto: {
          expiresOn: data.expiresOn ? formatUtcIsoDateTime(data.expiresOn.toDate()) : null,
          versionTag: ctx.props.versionTag,
        },
      }),
  })
}
